/**
 * 微信 JSSDK 封装
 * 用于公众号 H5 调用微信原生能力（chooseImage/uploadImage 等）
 * 主要解决 iOS WeChat WebView 拍照后页面刷新的内存问题
 */
import wx from "weixin-js-sdk";
import axios from "axios";
import Vue from "vue";

let configured = false;
let configuring = null;

/**
 * 从后端获取签名并调用 wx.config
 * 需要后端提供 GET /gzh/getJsApiSignature?url=<encodedCurrentUrl>
 * 返回 { appId, timestamp, nonceStr, signature }
 */
export function ensureWxConfig() {
  if (configured) return Promise.resolve(true);
  if (configuring) return configuring;

  // iOS 的 wx.config 必须用页面第一次进入时的 url（不含 hash）
  // 安卓用当前 url 即可。统一用 location.href.split('#')[0] 兼容两端
  const signUrl = location.href.split("#")[0];
  const baseUrl = Vue.prototype.GLOBAL.ssoServer;

  configuring = axios
    .get(baseUrl + "/gzh/getJsApiSignature", {
      params: { url: signUrl },
    })
    .then((res) => {
      const data = res.data || {};
      // 后端约定：code === "0000000" 才是成功
      if (
        Object.prototype.hasOwnProperty.call(data, "code") &&
        data.code !== "0000000"
      ) {
        throw new Error(
          "签名接口返回失败：" +
            (data.message || data.code || "unknown") +
            "（请让后端检查 IP 白名单、AppSecret、access_token 错误日志）"
        );
      }
      const payload = data.data || data;
      if (!payload.appId || !payload.signature) {
        throw new Error("签名接口返回格式不正确，缺少 appId/signature");
      }
      return new Promise((resolve, reject) => {
        wx.config({
          debug: false,
          appId: payload.appId,
          timestamp: payload.timestamp,
          nonceStr: payload.nonceStr,
          signature: payload.signature,
          jsApiList: [
            "chooseImage",
            "getLocalImgData",
            "uploadImage",
            "previewImage",
          ],
        });
        wx.ready(() => {
          configured = true;
          resolve(true);
        });
        wx.error((err) => {
          configured = false;
          configuring = null;
          console.warn("wx.config 失败", err);
          reject(err);
        });
      });
    })
    .catch((err) => {
      configuring = null;
      throw err;
    });

  return configuring;
}

/**
 * 是否在微信环境中
 */
export function isWxEnv() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf("micromessenger") > -1;
}

/**
 * 调用 wx.chooseImage 选图
 * @param {Object} options
 * @param {number} options.count 最多选几张，默认 9
 * @param {Array<string>} options.sourceType ['album','camera']
 * @returns {Promise<{localIds: string[]}>}
 */
export function chooseImage(options = {}) {
  return ensureWxConfig().then(
    () =>
      new Promise((resolve, reject) => {
        wx.chooseImage({
          count: options.count || 9,
          sizeType: options.sizeType || ["compressed"],
          sourceType: options.sourceType || ["album", "camera"],
          success: (res) => resolve(res),
          fail: (err) => reject(err),
          cancel: () => reject({ cancelled: true }),
        });
      })
  );
}

/**
 * 获取本地图片的 base64
 * @param {string} localId
 * @returns {Promise<string>} base64 dataUrl
 */
export function getLocalImgData(localId) {
  return new Promise((resolve, reject) => {
    wx.getLocalImgData({
      localId,
      success: (res) => {
        let data = res.localData || "";
        // iOS 返回的 base64 可能缺少 data: 前缀
        if (data && data.indexOf("data:image") !== 0) {
          data = "data:image/jpeg;base64," + data.replace(/^data:image\/\w+;base64,/, "");
        }
        // 部分机型返回的 base64 会带 jgp 拼写错误前缀
        data = data.replace("data:image/jgp", "data:image/jpeg");
        resolve(data);
      },
      fail: (err) => reject(err),
    });
  });
}

/**
 * base64 dataUrl 转 Blob，便于用 FormData 上传
 */
export function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(",");
  const match = /data:([^;]+);base64/.exec(parts[0]);
  const mime = match ? match[1] : "image/jpeg";
  const bin = atob(parts[1] || "");
  const len = bin.length;
  const buf = new Uint8Array(len);
  for (let i = 0; i < len; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: mime });
}
